import React, { useState } from 'react';
import { KeyRound, Mail, UserPlus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

const CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
const genPassword = () => {
  const buf = new Uint32Array(12); window.crypto.getRandomValues(buf);
  return Array.from(buf, n => CHARS[n % CHARS.length]).join('');
};

export const PasswordStatus = ({ u }) => (
  u.must_change_password
    ? <span className="text-xs text-amber-600" data-testid={`user-pw-status-${u.id}`}>Temporary, not changed yet</span>
    : <span className="text-xs text-slate-400" data-testid={`user-pw-status-${u.id}`}>Set by user</span>
);

const SetPasswordDialog = ({ u, isSelf, open, onOpenChange, onDone }) => {
  const { api } = useAuth();
  const [pw, setPw] = useState('');
  const [sendEmail, setSendEmail] = useState(!isSelf);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      const r = await api().post(`/users/${u.id}/set-password`, { new_password: pw, send_email: sendEmail && !isSelf, note, redirect_base: window.location.origin });
      const em = r.data.email;
      if (isSelf) toast.success('Password changed');
      else if (!em) toast.success('Password set. They will be asked to change it at next login.');
      else if (em.sent) toast.success(`Password set and emailed to ${u.email}`);
      else toast.warning(`Password set, but the email was not sent: ${em.detail}`, { duration: 12000 });
      onOpenChange(false); setPw(''); setNote(''); onDone?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? 'Password must be 8–128 characters' : (d || 'Failed'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="password-dialog">
        <DialogHeader><DialogTitle>Set Password — {u.full_name || u.email}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>{isSelf ? 'New password *' : 'New temporary password *'}</Label>
            <div className="flex gap-2">
              <Input type="text" autoComplete="off" value={pw} onChange={e => setPw(e.target.value)} placeholder="Min 8 characters" className="font-mono" data-testid="password-input" />
              <Button type="button" variant="outline" onClick={() => setPw(genPassword())} data-testid="password-generate-btn"><RefreshCw className="w-4 h-4 mr-1" />Generate</Button>
            </div>
            {!isSelf && <p className="text-xs text-slate-500">They'll be asked to choose their own password at next sign-in.</p>}
          </div>
          {!isSelf && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={sendEmail} onCheckedChange={v => setSendEmail(!!v)} data-testid="password-email-checkbox" />
              Email the temporary password to <b>{u.email}</b>
            </label>
          )}
          {!isSelf && sendEmail && (
            <div className="space-y-1">
              <Label>Note to include (optional)</Label>
              <Textarea rows={3} maxLength={1000} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Call me if you have trouble signing in." data-testid="password-note-input" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="password-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="password-save-btn">
            {saving ? 'Saving…' : (sendEmail && !isSelf ? 'Set & email' : 'Set password')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const UserPasswordActions = ({ u, isSelf, onChanged }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);

  const sendEmail = async (kind) => {
    try {
      const r = await api().post(`/users/${u.id}/${kind === 'invite' ? 'send-invite' : 'send-reset-link'}`, { redirect_base: window.location.origin });
      if (r.data.sent) { toast.success(`${kind === 'invite' ? 'Welcome email' : 'Reset link'} sent to ${u.email}`); if (kind === 'invite') onChanged?.(); }
      else toast.error(`Email not sent: ${r.data.detail}`, { duration: 12000 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-8 mx-1" data-testid={`user-password-menu-${u.id}`}><KeyRound className="w-4 h-4 mr-1" />Password &amp; email</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onClick={() => setOpen(true)} data-testid={`user-reset-${u.id}`}><KeyRound className="w-4 h-4 mr-2" />{isSelf ? 'Change my password…' : 'Set temporary password…'}</DropdownMenuItem>
          {!isSelf && <DropdownMenuItem onClick={() => sendEmail('reset')} data-testid={`user-email-reset-${u.id}`}><Mail className="w-4 h-4 mr-2 text-blue-600" />Email a reset link</DropdownMenuItem>}
          {!isSelf && (
            <DropdownMenuItem onClick={() => sendEmail('invite')} data-testid={`user-email-invite-${u.id}`}>
              <UserPlus className="w-4 h-4 mr-2 text-orange-600" />{u.invited_at ? `Resend welcome email (sent ${new Date(u.invited_at).toLocaleDateString()})` : 'Send welcome email'}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <SetPasswordDialog u={u} isSelf={isSelf} open={open} onOpenChange={setOpen} onDone={onChanged} />
    </>
  );
};
